import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Stack,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  useTheme,
  alpha
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Description as DescriptionIcon,
  Schedule as ScheduleIcon,
  Folder as FolderIcon
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useFolderContext } from '../context/FolderContext';
import { useAppTheme } from '../utils/themeUtils';

const MyDocuments = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { folders, selectedFolderId } = useFolderContext();
  const theme = useTheme();
  const { colors } = useAppTheme();
  
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchDocuments = async () => {
      if (!user?.userId) return;
      
      try {
        setLoading(true);
        const docs = await api.documents.getUserDocuments(
          user.userId, 
          selectedFolderId, 
          50
        );
        setDocuments(docs || []);
      } catch (err) {
        console.error('Error fetching documents:', err);
        setError('Failed to load documents. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchDocuments();
  }, [user?.userId, selectedFolderId]);

  const handleDelete = async (documentId) => {
    if (window.confirm('Are you sure you want to delete this document?')) {
      try {
        await api.documents.delete(documentId);
        setDocuments(prev => prev.filter(doc => doc.documentId !== documentId));
      } catch (err) {
        console.error('Error deleting document:', err);
        alert('Failed to delete document. Please try again.');
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    try {
      // Handle both ISO string and epoch timestamp
      let date;
      if (typeof dateString === 'string' && dateString.includes('T')) {
        date = new Date(dateString);
      } else if (typeof dateString === 'number' || (typeof dateString === 'string' && !isNaN(dateString))) {
        // Handle epoch timestamp (convert to milliseconds if needed)
        const timestamp = Number(dateString);
        date = new Date(timestamp > 1000000000000 ? timestamp : timestamp * 1000);
      } else {
        date = new Date(dateString);
      }
      
      // Check if date is valid and not epoch (1970)
      if (isNaN(date.getTime()) || date.getFullYear() === 1970) {
        return 'Recently';
      }
      
      return date.toLocaleDateString();
    } catch {
      return 'Invalid date';
    }
  };

  const getDocumentTypeColor = (type) => {
    switch (type) {
      case 'RICH_TEXT': return theme.palette.primary.main;
      case 'MARKDOWN': return theme.palette.secondary.main;
      case 'PLAIN_TEXT': return theme.palette.info.main;
      case 'CODE': return theme.palette.success.main;
      default: return theme.palette.grey[500];
    }
  };

  const getFolderName = (folderId) => {
    if (!folderId) return 'No Folder';
    const folder = folders.find(f => f.folderId === folderId);
    return folder?.name || 'Unknown Folder';
  };

  const filteredDocuments = documents.filter(doc =>
    doc.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    doc.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="60vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      width: '100%',
      minHeight: '100vh',
      backgroundColor: colors.background,
      p: 2
    }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header */}
        <Paper 
          elevation={2} 
          sx={{ 
            p: 3, 
            mb: 3,
            backgroundColor: colors.surface,
            borderRadius: 2
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 1 }}>
                My Documents
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {filteredDocuments.length} document{filteredDocuments.length !== 1 ? 's' : ''} found
                {selectedFolderId && ` in ${getFolderName(selectedFolderId)}`}
              </Typography>
            </Box>
            
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/docs')}
              sx={{ minWidth: 140 }}
            >
              New Document
            </Button>
          </Box>

          {/* Search */}
          <TextField
            fullWidth
            placeholder="Search documents by title or tags..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ maxWidth: 400 }}
          />
        </Paper>

        {/* Error State */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Empty State */}
        {!loading && filteredDocuments.length === 0 && (
          <Paper 
            elevation={1} 
            sx={{ 
              p: 6, 
              textAlign: 'center',
              backgroundColor: colors.surface,
              borderRadius: 2
            }}
          >
            <DescriptionIcon sx={{ fontSize: 64, color: colors.textSecondary, mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {searchTerm ? 'No documents found' : 'No documents yet'}
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              {searchTerm 
                ? 'Try adjusting your search terms'
                : 'Create your first document to get started'
              }
            </Typography>
            {!searchTerm && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate('/docs')}
              >
                Create Document
              </Button>
            )}
          </Paper>
        )}

        {/* Documents Grid */}
        {filteredDocuments.length > 0 && (
          <Grid container spacing={3}>
            <AnimatePresence>
              {filteredDocuments.map((doc, index) => (
                <Grid item xs={12} sm={6} md={4} key={doc.documentId}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                  >
                    <Card 
                      elevation={2}
                      sx={{ 
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        backgroundColor: colors.surface,
                        borderRadius: 2,
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: theme.shadows[4],
                        }
                      }}
                    >
                      <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                        {/* Document Type & Status */}
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                          <Chip 
                            label={doc.type || 'RICH_TEXT'}
                            size="small"
                            sx={{ 
                              backgroundColor: alpha(getDocumentTypeColor(doc.type), 0.1),
                              color: getDocumentTypeColor(doc.type),
                              fontWeight: 500
                            }}
                          />
                          {doc.isPublic && (
                            <Chip 
                              icon={<VisibilityIcon />}
                              label="Public" 
                              size="small" 
                              color="success"
                            />
                          )}
                        </Box>

                        {/* Title */}
                        <Typography 
                          variant="h6" 
                          component="h2" 
                          sx={{ 
                            mb: 1,
                            fontWeight: 600,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                          }}
                        >
                          {doc.title}
                        </Typography>

                        {/* Summary */}
                        {doc.summary ? (
                          <Typography 
                            variant="body2" 
                            color="textSecondary" 
                            sx={{ 
                              mb: 2,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                            }}
                          >
                            {doc.summary}
                          </Typography>
                        ) : (
                          <Typography 
                            variant="body2" 
                            color="textSecondary" 
                            sx={{ 
                              mb: 2,
                              fontStyle: 'italic',
                              opacity: 0.7
                            }}
                          >
                            Document saved successfully • Content stored in S3
                          </Typography>
                        )}

                        {/* Tags */}
                        {doc.tags && doc.tags.length > 0 && (
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mb: 2 }}>
                            {doc.tags.slice(0, 3).map((tag, tagIndex) => (
                              <Chip 
                                key={tagIndex}
                                label={tag}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.7rem', height: 20 }}
                              />
                            ))}
                            {doc.tags.length > 3 && (
                              <Chip 
                                label={`+${doc.tags.length - 3}`}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.7rem', height: 20 }}
                              />
                            )}
                          </Stack>
                        )}

                        {/* Metadata */}
                        <Stack spacing={0.5}>
                          <Box display="flex" alignItems="center">
                            <ScheduleIcon sx={{ fontSize: 14, mr: 0.5, color: colors.textSecondary }} />
                            <Typography variant="caption" color="textSecondary">
                              {formatDate(doc.updatedAt)}
                            </Typography>
                          </Box>
                          {doc.folderId && (
                            <Box display="flex" alignItems="center">
                              <FolderIcon sx={{ fontSize: 14, mr: 0.5, color: colors.textSecondary }} />
                              <Typography variant="caption" color="textSecondary">
                                {getFolderName(doc.folderId)}
                              </Typography>
                            </Box>
                          )}
                        </Stack>
                      </CardContent>

                      <CardActions sx={{ pt: 0, px: 2, pb: 2 }}>
                        <Button
                          size="small"
                          startIcon={<VisibilityIcon />}
                          onClick={() => navigate(`/document/${doc.documentId}`)}
                        >
                          View
                        </Button>
                        <Button
                          size="small"
                          startIcon={<EditIcon />}
                          onClick={() => navigate(`/docs?edit=${doc.documentId}`)}
                        >
                          Edit
                        </Button>
                        <Tooltip title="Delete Document">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(doc.documentId)}
                            sx={{ ml: 'auto' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </CardActions>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </AnimatePresence>
          </Grid>
        )}
      </motion.div>
    </Box>
  );
};

export default MyDocuments;
